/**
 * DesktopBilling — "Plan & billing" screen for the Scrivar desktop shell.
 *
 * Renders inside the shell's scrollable content area (.scrivar-desktop scroll).
 * Reproduces the mockup `.view[data-view="plan"]` layout:
 *   - Owned-plan card (label, "Owned" badge, included features, info note)   [owners only]
 *   - Free/trial card with "Choose a license" buy buttons                    [non-owners]
 *   - Cloud & AI add-on card (ALWAYS visible)
 *   - License/devices row
 *
 * The add-on card is intentionally always rendered so lifetime holders who
 * came via Amazon registration codes can discover and purchase it — this
 * directly addresses the "cloud subscription doesn't show up" complaint.
 */

import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { Cloud, History, Link2, MessageSquare, Zap, BadgeCheck, ShieldCheck, Sparkles, Check, Info, ExternalLink } from 'lucide-react';
import { useBilling } from '@/hooks/useBilling';
import { getTrialDaysRemaining } from '@/lib/license';
import { openExternalUrl, SCRIVAR_BILLING_URL } from '@/lib/external';

// ─── Feature lists ────────────────────────────────────────────────────────────

const OWNED_FEATURES = [
  'All 30+ PDF tools',
  'Full text & image editor',
  'OCR & batch processing',
  'Invoice builder',
  'Unlimited eSign requests',
  'Works offline',
];

const ADDON_FEATURES = [
  { icon: Cloud,          text: 'Cloud sync across all your devices' },
  { icon: History,        text: '30-day version history' },
  { icon: Link2,          text: 'Shareable view & edit links' },
  { icon: MessageSquare,  text: 'AI chat, summarize & translate' },
  { icon: Zap,            text: 'Priority processing' },
];

// Maps tier strings to a display label for the owned-plan card.
const TIER_DISPLAY = {
  lifetime_pro:  'Pro Lifetime',
  lifetime_lite: 'Lite Lifetime',
  pro:           'Pro',
  lite:          'Lite',
  free:          'Free',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function OwnedPlanCard({ tier }) {
  const label = TIER_DISPLAY[tier] || tier;

  return (
    <div style={{
      background: 'var(--ds-surface, #fff)',
      border: '1px solid var(--ds-border, #EAEBF0)',
      borderRadius: 'var(--ds-radius, 12px)',
      padding: '20px',
      boxShadow: 'var(--ds-shadow-card, 0 1px 2px rgba(20,22,48,.05))',
    }}>
      {/* Row 1: plan name + Owned badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0, color: 'var(--ds-ink, #191A24)' }}>
          {label}
        </h3>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          fontSize: '12px', fontWeight: 600,
          color: 'var(--ds-green, #15803D)',
          background: 'var(--ds-green-bg, #ECFDF3)',
          border: '1px solid var(--ds-green-line, #BBF0CE)',
          borderRadius: '999px', padding: '2px 10px',
        }}>
          <BadgeCheck size={13} />
          Owned
        </span>
      </div>

      {/* Subtext */}
      <p style={{ fontSize: '13px', color: 'var(--ds-ink-2, #565869)', margin: '6px 0 14px', lineHeight: 1.5 }}>
        One-time purchase — <strong>yours forever</strong>, no subscription. Activated on this Mac and usable on the web.
      </p>

      {/* Features checklist */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
        {OWNED_FEATURES.map((feat) => (
          <div key={feat} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--ds-ink, #191A24)' }}>
            <Check size={13} color="var(--ds-green, #15803D)" />
            {feat}
          </div>
        ))}
      </div>

      {/* Info note */}
      <div style={{
        display: 'flex', gap: '8px', alignItems: 'flex-start',
        marginTop: '14px',
        background: 'var(--ds-indigo-50, #EEF0FE)',
        border: '1px solid var(--ds-indigo-100, #E2E5FC)',
        borderRadius: '8px',
        padding: '10px 12px',
        fontSize: '12.5px',
        color: 'var(--ds-ink-2, #565869)',
        lineHeight: 1.5,
      }}>
        <Info size={14} style={{ flexShrink: 0, marginTop: '1px', color: 'var(--ds-indigo, #4F46E5)' }} />
        <p style={{ margin: 0 }}>
          <strong>Cloud storage &amp; AI tools aren't part of a lifetime license.</strong>{' '}
          Add them any time with the optional subscription on the right — it layers on top, nothing here changes.
        </p>
      </div>
    </div>
  );
}

function FreePlanCard() {
  const trialDays = getTrialDaysRemaining();
  const onTrial = trialDays !== null && trialDays > 0;

  return (
    <div style={{
      background: 'var(--ds-surface, #fff)',
      border: '1px solid var(--ds-border, #EAEBF0)',
      borderRadius: 'var(--ds-radius, 12px)',
      padding: '20px',
      boxShadow: 'var(--ds-shadow-card, 0 1px 2px rgba(20,22,48,.05))',
    }}>
      {/* Heading */}
      <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 6px', color: 'var(--ds-ink, #191A24)' }}>
        {onTrial ? 'Free trial' : 'Free plan'}
      </h3>
      <p style={{ fontSize: '13px', color: 'var(--ds-ink-2, #565869)', margin: '0 0 18px', lineHeight: 1.5 }}>
        {onTrial
          ? `${trialDays} day${trialDays === 1 ? '' : 's'} left — full Pro tools during your trial.`
          : 'Core PDF tools with daily limits. No watermarks.'}
      </p>

      {/* Choose a license */}
      <div style={{
        background: 'var(--ds-sidebar, #F4F5F8)',
        border: '1px solid var(--ds-border, #EAEBF0)',
        borderRadius: '10px',
        padding: '14px 16px',
      }}>
        <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ds-ink-2, #565869)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '.4px' }}>
          Choose a license
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => openExternalUrl(SCRIVAR_BILLING_URL)}
            style={{
              flex: 1,
              background: 'var(--ds-surface, #fff)',
              border: '1px solid var(--ds-border-2, #D4D7E3)',
              borderRadius: '8px',
              padding: '10px 14px',
              fontSize: '13px', fontWeight: 600,
              color: 'var(--ds-ink, #191A24)',
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            Buy Lite — $49
          </button>
          <button
            type="button"
            onClick={() => openExternalUrl(SCRIVAR_BILLING_URL)}
            style={{
              flex: 1,
              background: 'var(--ds-indigo, #4F46E5)',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 14px',
              fontSize: '13px', fontWeight: 600,
              color: 'var(--ds-content, #fff)',
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            Buy Pro — $70
          </button>
        </div>
        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--ds-ink-4, #A9ABB8)' }}>
          <ExternalLink size={11} />
          Opens in your browser
        </div>
      </div>
    </div>
  );
}

function AddonCard({ addonSubscription, isOwner }) {
  const isActive = Boolean(addonSubscription);

  return (
    <div style={{
      background: 'var(--ds-surface, #fff)',
      border: '1px solid var(--ds-border, #EAEBF0)',
      borderRadius: 'var(--ds-radius, 12px)',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      boxShadow: 'var(--ds-shadow-card, 0 1px 2px rgba(20,22,48,.05))',
    }}>
      {/* Top: badge + heading + price + blurb */}
      <div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          fontSize: '11px', fontWeight: 600, letterSpacing: '.3px',
          color: 'var(--ds-indigo, #4F46E5)',
          background: 'var(--ds-indigo-50, #EEF0FE)',
          borderRadius: '999px', padding: '3px 10px', marginBottom: '10px',
        }}>
          <Sparkles size={12} />
          Optional add-on
        </div>
        {/*
          The price is displayed as a single text node inside one element so
          getByText(/\$4\.99/) finds exactly one match.
          We intentionally do NOT split "$4.99" and "/ month" into sibling spans
          because that produces two DOM elements both matching the regex.
        */}
        <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--ds-ink, #191A24)', marginBottom: '6px' }}>
          $4.99{' '}
          <span style={{ fontSize: '13px', fontWeight: 400, color: 'var(--ds-ink-3, #8C8FA0)' }}>/ month</span>
        </div>
        <p style={{ fontSize: '13px', color: 'var(--ds-ink-2, #565869)', margin: 0, lineHeight: 1.5 }}>
          {isOwner
            ? 'Adds cloud sync and AI tools on top of your lifetime license. Cancel any time.'
            : 'Requires a lifetime license (Lite or Pro). Buy a license first, then add this.'}
        </p>
      </div>

      {/* Feature list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {ADDON_FEATURES.map(({ icon: Icon, text }) => (
          <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--ds-ink, #191A24)' }}>
            <Icon size={14} color="var(--ds-indigo, #4F46E5)" />
            {text}
          </div>
        ))}
      </div>

      {/* Footer: CTA or active state */}
      <div style={{ marginTop: 'auto' }}>
        {isActive ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            fontSize: '13px', fontWeight: 600,
            color: 'var(--ds-green, #15803D)',
            background: 'var(--ds-green-bg, #ECFDF3)',
            border: '1px solid var(--ds-green-line, #BBF0CE)',
            borderRadius: '8px', padding: '10px 14px',
          }}>
            <BadgeCheck size={15} />
            <span>You have Cloud &amp; AI — Active</span>
          </div>
        ) : (
          <>
            {/*
              aria-label carries the full "Add Cloud & AI — $4.99/mo" name so
              getByRole('button', { name: /add cloud & ai/i }) resolves via
              accessible name rather than text content.
              The visible text intentionally omits the price so the button's
              text content does not also match getByText(/\$4\.99/) — which
              would cause a "multiple elements" error alongside the price display.
            */}
            <button
              type="button"
              aria-label="Add Cloud & AI — $4.99/mo"
              onClick={() => openExternalUrl(SCRIVAR_BILLING_URL)}
              style={{
                display: 'block', width: '100%',
                background: isOwner ? 'var(--ds-indigo, #4F46E5)' : 'var(--ds-border, #EAEBF0)',
                color: isOwner ? 'var(--ds-content, #fff)' : 'var(--ds-ink-3, #8C8FA0)',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 16px',
                fontSize: '13px', fontWeight: 600,
                cursor: isOwner ? 'pointer' : 'default',
                textAlign: 'center',
                pointerEvents: isOwner ? 'auto' : 'none',
              }}
            >
              Add Cloud &amp; AI
            </button>
            <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--ds-ink-4, #A9ABB8)', textAlign: 'center' }}>
              {isOwner ? 'Billed monthly · cancel any time' : 'Requires a lifetime license'}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LicenseCard({ isOwner, onManage }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '12px',
      background: 'var(--ds-surface, #fff)',
      border: '1px solid var(--ds-border, #EAEBF0)',
      borderRadius: 'var(--ds-radius, 12px)',
      padding: '14px 18px',
      marginTop: '12px',
      boxShadow: 'var(--ds-shadow-card, 0 1px 2px rgba(20,22,48,.05))',
    }}>
      <div style={{
        flexShrink: 0,
        width: '36px', height: '36px',
        background: isOwner ? 'var(--ds-green-bg, #ECFDF3)' : 'var(--ds-sidebar, #F4F5F8)',
        borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <ShieldCheck size={18} color={isOwner ? 'var(--ds-green, #15803D)' : 'var(--ds-ink-3, #8C8FA0)'} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ds-ink, #191A24)' }}>
          {isOwner ? 'This device is activated' : 'No license on this device'}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--ds-ink-3, #8C8FA0)', marginTop: '2px' }}>
          {isOwner ? '1 desktop + 1 web session' : 'Buy a license to activate'}
        </div>
      </div>
      <button
        type="button"
        onClick={onManage}
        style={{
          fontSize: '12px', fontWeight: 600,
          color: 'var(--ds-indigo, #4F46E5)',
          background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px',
        }}
      >
        {isOwner ? 'Manage devices' : 'Learn more'}
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DesktopBilling() {
  const navigate = useNavigate();
  const { tier, addonSubscription, hasLifetime } = useBilling();

  return (
    <div style={{ padding: '24px', maxWidth: '860px' }}>
      {/* View header */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 4px', color: 'var(--ds-ink, #191A24)' }}>
          Plan &amp; billing
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--ds-ink-2, #565869)', margin: 0 }}>
          {hasLifetime
            ? 'What you own, and the optional subscription you can add to it.'
            : 'Choose a lifetime license to unlock the full Scrivar toolset.'}
        </p>
      </div>

      {/* Two-column plan grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>
        {/* Left column: plan card + license row */}
        <div>
          {hasLifetime
            ? <OwnedPlanCard tier={tier} />
            : <FreePlanCard />}
          <LicenseCard
            isOwner={hasLifetime}
            onManage={() => navigate('/account/devices')}
          />
        </div>

        {/* Right column: always-visible add-on card */}
        <AddonCard addonSubscription={addonSubscription} isOwner={hasLifetime} />
      </div>
    </div>
  );
}
